import fetch from 'cross-fetch';
import {
  ApolloLink, createHttpLink, ApolloClient, split, DocumentTransform,
} from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { InMemoryCache } from '@apollo/client/cache';
import { setContext } from '@apollo/client/link/context';
import * as merge from 'lodash/merge';

import { login } from '@urls';
import { CSRF_TOKEN_HEADER_NAME, USER_BANNED_FRAUD_ERROR_MESSAGE } from '@constants';
import { getCSRFToken } from '@utils/common';
import {
  isAuthError,
  SentryGraphQLError,
  SentryNetworkError,
} from '@utils/errors';
import {
  dataIdFromObject,
  typePolicies,
} from '@utils/apollo';
import { logger } from '@utils/logger';
import { getSentry } from '@utils/sentry/initSentryBrowser';

// consts
const webPrefix = 'WEB_';
export const uri3 = '/api/v3/graphiql';
export const uri5 = '/api/v5/graphql';
export const defaultHeaders = {
  'x-app-platform': 'web',
  'x-app-version': SENTRY_RELEASE,
  [CSRF_TOKEN_HEADER_NAME]: getCSRFToken(),
};
const batchUri3 = '/api/v3/graphiql-batch';
const uri4 = '/api/v4/graphiql/';

const authLink = setContext((_, { headers, context }) => ({
  headers: {
    ...headers,
    [CSRF_TOKEN_HEADER_NAME]: getCSRFToken(),
  },
  ...context,
}));

const httpLink3 = createHttpLink({
  fetch,
  uri: uri3,
  headers: defaultHeaders,
  credentials: 'include',
});
const httpLink5 = createHttpLink({
  fetch,
  uri: uri5,
  headers: defaultHeaders,
  credentials: 'include',
});
const batchHttpLink3 = new BatchHttpLink({
  fetch,
  uri: batchUri3,
  headers: {
    ...defaultHeaders,
    batch: 'true',
  },
  credentials: 'include',
});
const httpLink4 = createHttpLink({
  fetch,
  uri: uri4,
  headers: defaultHeaders,
  credentials: 'include',
});
const batchHttpLink4 = new BatchHttpLink({
  fetch,
  uri: uri4,
  headers: defaultHeaders,
  credentials: 'include',
});

const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
  if (graphQLErrors?.length) {
    // Handle authentication error
    const authErrorIndex = graphQLErrors.findIndex(isAuthError);
    if (authErrorIndex !== -1) {
       
      graphQLErrors = [
        ...graphQLErrors.slice(0, authErrorIndex),
        ...graphQLErrors.slice(authErrorIndex + 1, graphQLErrors.length),
      ];

      // Make redirect on login, if auth error not ignored
      if (!operation.getContext()?.ignoreAuthError) {
        const searchParams = new URLSearchParams({ next: location.href });
        if (!location.pathname.startsWith(login)) {
          location.href = `${login}?${searchParams.toString()}`;
        } else {
          logger.error('Attempt to make a circular redirect to login page');
        }
      }
    }

    let errorsToLog = [...graphQLErrors];
    if (operation.getContext()?.loggerIgnoreBannedErrors) {
      errorsToLog = errorsToLog.filter((obj) => obj.message !== USER_BANNED_FRAUD_ERROR_MESSAGE);
    }
    // Log rest errors if they exist
    if (errorsToLog.length) {
      const exception = new SentryGraphQLError(operation, errorsToLog);

      // Add extra data to message, group graphql error by operation
      getSentry().then(
        (Sentry) => {
          Sentry.withScope(
            (scope) => {
              scope.setExtras({
                operationVariables: operation.variables,
                context: operation.extensions,
              });
              scope.setFingerprint([
                exception.name,
                operation.operationName,
                exception.message,
              ]);

              Sentry.captureException(exception);
            },
          );
        },
      );
    }
  }
  if (networkError) {
    const exception = new SentryNetworkError(operation, networkError);

    // Group all network errors into single issue
    getSentry().then(
      (Sentry) => {
        Sentry.withScope(
          (scope) => {
            scope.setFingerprint([exception.name]);
            Sentry.captureException(exception);
          },
        );
      },
    );
  }
});

const documentTransform = new DocumentTransform((document) => {
  const transformedDocument = {
    ...document,
    definitions: document.definitions.map(definition => {
      if (definition.kind === 'OperationDefinition' && definition.name) {
        if (!definition.name.value.startsWith(webPrefix)) {
          return {
            ...definition,
            name: {
              ...definition.name,
              value: `${webPrefix}${definition.name.value}`
            }
          };
        }
      }
      return definition;
    })
  };

  return transformedDocument;
});

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: 5000,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error, operation) => {
      const isContainMutation = operation.query.definitions.some(
        (definition) => (
          'operation' in definition
          && definition.operation === 'mutation'
        ),
      );

      // Never retry request if it contain mutation
      if (isContainMutation) {
        return false;
      }

      return !!error;
    },
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let client: ApolloClient<any>;
if (typeof window !== 'undefined' && window.__APOLLO_CLIENT__) {
  client = window.__APOLLO_CLIENT__;
} else {
  client = new ApolloClient({
    dataMasking: true,
    documentTransform,
    ssrForceFetchDelay: typeof window !== 'undefined' && window.__APOLLO_STATE__ ? 1000 : undefined,
    link: ApolloLink.from([
      authLink,
      errorLink,
      retryLink,
      split(
        (operation) => operation.getContext().v4 === true,
        split(
          (operation) => operation.getContext().batch === true,
          batchHttpLink4,
          httpLink4,
        ),
        split(
          (operation) => operation.getContext().v5 === true,
          httpLink5,
          split(
            (operation) => operation.getContext().batch === true,
            batchHttpLink3,
            httpLink3,
          ),
        ),

      ),
    ]),
    cache:
      new InMemoryCache({
        dataIdFromObject,
        typePolicies,
      })
         
        .restore(
          typeof window !== 'undefined'
            ? merge(
              window.__APOLLO_STATE__ || {},
              window.__NAVBAR_APOLLO_STATE__ || {},
            )
            : {},
        ),
    defaultOptions: {
      watchQuery: {
        nextFetchPolicy: 'cache-first',
      },
    },
  });

  // To allow apollo client sharing between bundles
  if (typeof window !== 'undefined') {
    window.__APOLLO_CLIENT__ = client;
  }
}

export { client };
